/**
 * Tier-4 firing (M21, docs/TOWERS_V2.md "Tier 4"): the shot shapes a branch changes (splash, chain, pierce, a second
 * bolt, a flame cone, a rocket salvo, the Tesla's charge-and-discharge), the fire interval with haste and Overheat, and
 * the per-tower skill timers (Overheat, Volley, Rally Cry, Polymorph, Banish, the zone skills).
 */
import { TowerArchetype } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared } from "../../world/roadGeometry";
import { SimConstants } from "../constants";
import { Enemy, Tower, ZoneKind } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { applyHit, canTarget, findTarget, findTargetExcept } from "../systems/combat";
import { BASE_TIERS, branchOf, skill } from "./ascension";
import { beginShot, endShot } from "./hits";
import { layZone } from "./statuses";

/** The fire interval of `t` in ticks: its branch's multiplier, Overheat, then gear fire rate and aura haste (never under 1). */
export function fireInterval(sim: LevelSim, t: Tower, base: number, gearRate: Fix64): number {
  let ticks = base;
  const b = t.tier > BASE_TIERS ? branchOf(sim, t) : null;
  if (b !== null) {
    const m = b.tier4.num("fireIntervalMult", Fix64.One);
    if (!m.eq(Fix64.One)) ticks = Math.max(1, Fix64.fromInt(ticks).mul(m).floorToInt());
    const heat = t.skillActive > 0 ? skill(sim, t, "overheat") : null;
    if (heat !== null) ticks = Math.max(1, Fix64.fromInt(ticks).mul(heat.params.num("intervalMult", Fix64.One)).floorToInt());
  }
  const bonus = gearRate.add(t.auraHaste).add(t.rallyHaste);
  if (bonus.isZero()) return ticks;
  return Math.max(1, Fix64.fromInt(ticks).div(Fix64.One.add(bonus)).floorToInt());
}

/** Splash radius: the archetype's × the branch's splashMult, + its splashBonus. */
export function splashOf(sim: LevelSim, t: Tower, a: TowerArchetype): Fix64 {
  const b = branchOf(sim, t);
  if (b === null) return a.splashRadius;
  return a.splashRadius.mul(b.tier4.num("splashMult", Fix64.One)).add(b.tier4.num("splashBonus"));
}
/** Chain hits: + the Storm's chainBonus. */
export function chainTargetsOf(sim: LevelSim, t: Tower, a: TowerArchetype): number { return a.chainTargets + (branchOf(sim, t)?.tier4.int("chainBonus") ?? 0); }
/** Chain falloff: 1 with Overload. */
export function chainFalloffOf(sim: LevelSim, t: Tower, a: TowerArchetype): Fix64 { return skill(sim, t, "overload")?.params.num("falloff", a.chainFalloff) ?? a.chainFalloff; }
/** Pierce: + the Lance's pierceBonus and its wider line. */
export function pierceCountOf(sim: LevelSim, t: Tower, a: TowerArchetype): number { return a.pierceCount + (branchOf(sim, t)?.tier4.int("pierceBonus") ?? 0); }
export function pierceWidthOf(sim: LevelSim, t: Tower, a: TowerArchetype): Fix64 { return a.pierceWidth.add(branchOf(sim, t)?.tier4.num("pierceWidthBonus") ?? Fix64.Zero); }

/**
 * A tier-4 tower whose turn is not an ordinary shot: the Tesla charges and discharges, the Blizzard has no shot (its aura
 * works in sim/branches/auras.ts). Returns true when it has dealt with the tower this tick.
 */
export function branchTurn(sim: LevelSim, t: Tower, a: TowerArchetype): boolean {
  const b = branchOf(sim, t);
  if (b === null) return false;
  if (b.id === "blizzard") return true;
  if (b.id !== "tesla") return false;
  const full = teslaChargeTicks(sim, t);
  if (t.charge < full) { t.charge++; return true; }
  const target = findTarget(sim, t);
  if (target === null) return true;
  discharge(sim, t, a, target, b.tier4.int("targets"), b.tier4.num("arcRange"), sim.damageOf(t).mul(b.tier4.num("dischargeMult", Fix64.One)));
  t.charge = 0;
  return true;
}

/** A tier-4 Tesla Coil's full charge in ticks (Supercharge shortens it); 0 for any other tower. Views read it for the charge clip. */
export function teslaChargeTicks(sim: LevelSim, t: Tower): number {
  const b = t.tier > BASE_TIERS ? branchOf(sim, t) : null;
  if (b === null || b.id !== "tesla") return 0;
  const sc = skill(sim, t, "supercharge");
  return sc === null ? b.tier4.int("chargeTicks") : Fix64.fromInt(b.tier4.int("chargeTicks")).mul(sc.params.num("chargeMult", Fix64.One)).floorToInt();
}

/** The Tesla's discharge: its target, then the nearest others it can hit within `arc` of the target, up to `count`. */
function discharge(sim: LevelSim, t: Tower, a: TowerArchetype, target: Enemy, count: number, arc: Fix64, damage: Fix64): void {
  const r2 = arc.mul(arc);
  const others: Enemy[] = [];
  for (const e of sim.enemySlots) {
    if (!e.alive || e === target || !canTarget(a, e) || !sim.targetable(e)) continue;
    if (distanceSquared(target.x, target.y, e.x, e.y).gt(r2)) continue;
    others.push(e);
  }
  others.sort((p, q) => distanceSquared(target.x, target.y, p.x, p.y).cmp(distanceSquared(target.x, target.y, q.x, q.y)) || p.id - q.id);
  const hit = [target, ...others.slice(0, Math.max(0, count - 1))];
  sim.events.add(SimEventKind.TowerFired, t.id, target.id);
  sim.events.add(SimEventKind.TeslaDischarged, t.id, target.id, hit.length);
  beginShot(sim, t);
  sim.areaDepth++;
  for (let i = 0; i < hit.length; i++) {
    if (i > 0) sim.events.add(SimEventKind.ChainHit, hit[i].id, t.id, i);
    if (hit[i].alive) applyHit(sim, hit[i], t, a, damage);
  }
  sim.areaDepth--;
  endShot(sim);
}

/**
 * The Inferno's flame wave: every enemy it can hit within its range and inside the cone toward the target (cos² of the
 * half-angle `coneCos2`) takes the shot, the target included. True when it fired a cone.
 */
export function fireCone(sim: LevelSim, t: Tower, a: TowerArchetype, target: Enemy, damage: Fix64): boolean {
  const b = branchOf(sim, t);
  if (b === null || b.id !== "inferno") return false;
  const tx = Fix64.fromInt(t.cell.x), ty = Fix64.fromInt(t.cell.y);
  const dx = target.x.sub(tx), dy = target.y.sub(ty);
  const d2 = dx.mul(dx).add(dy.mul(dy));
  const r = sim.rangeOf(t), r2 = r.mul(r), cos2 = b.tier4.num("coneCos2");
  sim.areaDepth++;                                            // a flame wave is area damage (M30 #14), its target included
  for (const e of sim.enemySlots) {
    if (!e.alive || !canTarget(a, e) || !sim.targetable(e)) continue;
    if (e !== target) {
      const ex = e.x.sub(tx), ey = e.y.sub(ty);
      const e2 = ex.mul(ex).add(ey.mul(ey));
      const dot = ex.mul(dx).add(ey.mul(dy));
      if (e2.gt(r2) || !dot.isPositive() || dot.mul(dot).lt(cos2.mul(e2).mul(d2))) continue;
    }
    applyHit(sim, e, t, a, damage);
  }
  sim.areaDepth--;
  return true;
}

/** The Twin Repeater's second bolt: at the best other target (the same one when it has no other), as a full shot. */
export function secondBoltTarget(sim: LevelSim, t: Tower, first: Enemy): Enemy | null {
  const b = branchOf(sim, t);
  if (b === null || b.tier4.int("bolts") < 2) return null;
  return findTargetExcept(sim, t, first.id) ?? (first.alive ? first : null);
}

/**
 * After a tier-4 shot: the zone skills that ride a shot on their own cooldown (Thorn Trap, Firestorm, the Plague
 * Cauldron's cloud), and a Volley burst's next quick shot.
 */
export function afterShot(sim: LevelSim, t: Tower, target: Enemy, damage: Fix64): void {
  const b = branchOf(sim, t);
  if (b === null) return;
  if (t.burstLeft > 0) { t.burstLeft--; t.cooldown = Math.max(1, skill(sim, t, "volley")?.params.int("gapTicks", 1) ?? 1); }
  if (t.zoneTimer > 0) return;
  switch (b.id) {
    case "ranger": {
      const s = skill(sim, t, "thornTrap");
      if (s === null || target.isAir) return;
      zoneOn(sim, t, ZoneKind.Thorns, target, s.params.num("radius"), damage.mul(s.params.num("dpsMult")), s.params.num("slowFraction"), s.params.int("zoneTicks"), s.params.int("cooldownTicks"));
      return;
    }
    case "inferno": {
      const s = skill(sim, t, "firestorm");
      if (s === null) return;
      zoneOn(sim, t, ZoneKind.Firestorm, target, s.params.num("radius"), damage.mul(s.params.num("dpsMult")), Fix64.Zero, s.params.int("zoneTicks"), s.params.int("cooldownTicks"));
      return;
    }
    case "plague": {
      const m = skill(sim, t, "miasma");
      const radius = b.tier4.num("cloudRadius").add(m?.params.num("radiusBonus") ?? Fix64.Zero);
      const ticks = b.tier4.int("cloudTicks") + (m?.params.int("ticksBonus") ?? 0);
      zoneOn(sim, t, ZoneKind.PlagueCloud, target, radius, damage.mul(b.tier4.num("cloudDpsMult")), Fix64.Zero, ticks, b.tier4.int("cloudCooldownTicks"));
      return;
    }
    default: return;
  }
}

/** Lays a zone under `target` with `dps` (damage per second) and starts the tower's zone cooldown. */
function zoneOn(sim: LevelSim, t: Tower, kind: ZoneKind, target: Enemy, radius: Fix64, dps: Fix64, slow: Fix64, ticks: number, cooldown: number): void {
  layZone(sim, t, kind, target.x, target.y, radius, dps.divInt(SimConstants.TicksPerSecond), slow, ticks);
  t.zoneTimer = cooldown;
}

/**
 * Once per wave tick, for every tier-4 tower: its skill clocks run (a disabled tower's too; what they trigger waits for
 * a working tower). Overheat opens its window; Volley arms a burst; Rally Cry, Polymorph and Banish are handled by their
 * owners (auras.ts, the beam, the totem) off the same `skillTimer`/`skillActive`.
 */
export function tickTowerClocks(sim: LevelSim): void {
  for (const t of sim.towerSlots) {
    if (!t.alive || t.tier <= BASE_TIERS) continue;
    if (t.zoneTimer > 0) t.zoneTimer--;
    if (t.skillActive > 0) t.skillActive--;
    if (t.skillTimer > 0) { t.skillTimer--; continue; }
    if (t.disabledTicksLeft > 0) continue;
    const heat = skill(sim, t, "overheat");
    if (heat !== null) {
      t.skillActive = heat.params.int("activeTicks");
      t.skillTimer = heat.params.int("cooldownTicks");
      sim.events.add(SimEventKind.SkillFired, t.id, heat.index, 0);
      continue;
    }
    const volley = skill(sim, t, "volley");
    if (volley !== null) {
      t.burstLeft = volley.params.int("shots");
      t.skillTimer = volley.params.int("cooldownTicks");
      t.cooldown = 0;
      sim.events.add(SimEventKind.SkillFired, t.id, volley.index, 0);
    }
  }
}
